// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Event types this application declares, linking to the event type editor (AREA-EVENTS).
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Search, Workflow, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { EventTypeSummary } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { appEventTypesQuery } from '@/features/ops/api'
import { CreateEventTypeDialog } from '@/features/ops/CreateEventTypeDialog'
import { ProposalCountBadge } from '@/features/proposals/ProposalCountBadge'
import { formatNumber } from '@/lib/format'

export const Route = createFileRoute('/applications/$appId/event-types')({
  staticData: { crumb: 'Event types' },
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
  }),
  component: AppEventTypesPage,
})

const VALIDATION_LABEL = { off: 'Off', warn: 'Warn', enforce: 'Enforce' } as const

const col = tableColumns<EventTypeSummary>()
const columns = col.columns([
  col.accessor('key', {
    header: 'Event type',
    cell: (c) => (
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-[13px] text-ink">{c.getValue()}</span>
          <ProposalCountBadge
            count={c.row.original.open_proposals}
            to={{
              kind: 'event_type',
              id: c.row.original.id,
              app: c.row.original.application_id,
            }}
          />
        </div>
        <div className="truncate text-xs text-ink-3">{c.row.original.name}</div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor('enabled', {
    header: 'Status',
    cell: (c) => (c.getValue() ? <Badge tone="success">Enabled</Badge> : <Badge>Disabled</Badge>),
    meta: { width: '7rem' },
  }),
  col.accessor('rule_count', {
    header: 'Rules',
    cell: (c) =>
      c.getValue() === 0 ? <span className="text-warning">None</span> : formatNumber(c.getValue()),
    meta: { align: 'right', width: '6rem' },
  }),
  col.accessor((e) => e.samples.length, {
    id: 'samples',
    header: 'Samples',
    cell: (c) => formatNumber(c.getValue()),
    meta: { align: 'right', width: '6rem' },
  }),
  col.accessor('validation', {
    header: 'Validation',
    cell: (c) => (
      <span className={c.getValue() === 'enforce' ? 'text-ink' : 'text-ink-2'}>
        {VALIDATION_LABEL[c.getValue()]}
        {!c.row.original.schema && c.getValue() !== 'off' && (
          <span className="text-ink-3"> (no schema)</span>
        )}
      </span>
    ),
    meta: { width: '9rem' },
  }),
  col.accessor('dispatch', {
    header: 'Dispatch',
    cell: (c) => (c.getValue() === 'all_matches' ? 'All matches' : 'First match'),
    meta: { width: '8rem' },
  }),
  col.accessor('updated_at', {
    header: 'Updated',
    cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
    sortFn: 'datetime',
    meta: { align: 'right', width: '8rem' },
  }),
])

function AppEventTypesPage() {
  const { appId } = Route.useParams()
  const { q = '' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { can } = usePermissions()
  const [creating, setCreating] = useState(false)
  const list = useQuery(appEventTypesQuery(appId))

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const items = list.data ?? []
    if (!needle) return items
    return items.filter((e) => e.key.includes(needle) || e.name.toLowerCase().includes(needle))
  }, [list.data, q])

  const setQ = (v: string) =>
    void navigate({ search: (prev) => ({ ...prev, q: v || undefined }), replace: true })

  const newButton = (
    <Can permission="edit_content" mode="disable">
      <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
        New event type
      </Button>
    </Can>
  )

  return (
    <>
      <DataTable
        aria-label="Event types"
        columns={columns}
        data={filtered}
        getRowId={(e) => e.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        initialSorting={[{ id: 'key', desc: false }]}
        persistKey="app-event-types"
        onRowClick={(e) =>
          void navigate({
            to: '/event-types/$eventTypeId',
            params: { eventTypeId: e.id },
            search: { app: appId },
          })
        }
        toolbar={
          <>
            <Input
              className="w-64"
              size="sm"
              placeholder="Filter by key or name"
              aria-label="Filter event types"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              leading={<Search />}
              trailing={
                q ? (
                  <IconButton
                    label="Clear filter"
                    size="xs"
                    tooltip={false}
                    onClick={() => setQ('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <div className="ml-auto">{newButton}</div>
          </>
        }
        empty={
          q ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching event types"
              description={`Nothing matches “${q}”.`}
            />
          ) : (
            <EmptyState
              icon={Workflow}
              title="No event types yet"
              description="An event type, such as order.shipped, is something this application posts to MailSmith. Its rules decide which emails go out."
              action={
                can('edit_content') ? (
                  <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                    New event type
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
      <CreateEventTypeDialog appId={appId} open={creating} onOpenChange={setCreating} />
    </>
  )
}
